import { useCallback } from "react";
import { ViewSidebar, YouTube, MusicNote, GraphicEq, Gif, AudioFile, AutoFixHigh } from "@mui/icons-material";
import { Box, Button, ButtonProps, Tooltip } from "@mui/material";
import Grid from "@mui/system/Grid";

import { useAppContext } from "../../../hooks/useAppContext";
import useEditVideoFile from "../../../backend/useEditVideoFile";
import LinearProgressWithLabel from "./VideoLogoEditor/LinearProgressWithLabel";

// 1. Import the Autonomous Brain
import { useKernel } from "../../../hooks/AIKernel"; // Adjust the relative path as needed

const VideoEditorToolbar: React.FC = () => {
  const { mobileSidebarToggle, logoEditorToggle, isProcessing, videoRef } = useAppContext();

  // Import the functions from our upgraded hook
  const { 
    progress, 
    AIprocessVideoFullCorrection, 
    extractAudio, 
    generateGif, 
    addBackgroundAudio 
  } = useEditVideoFile();

  // 2. Connect to the Agent's state and sensory inputs
  const { agentState, reportBehavior } = useKernel();

  // 3. Adaptive Theme Variables
  const isDarkMode = agentState?.theme === "theme-cinematic-dark";
  const highlightCTA = agentState?.ui?.highlightCTAs;

  const handleMobileSidebarToggle = useCallback<NonNullable<ButtonProps["onClick"]>>(
    (event) => {
      event.preventDefault();
      mobileSidebarToggle();
    },
    [mobileSidebarToggle]
  );

  const handleLogoEditorToggle = useCallback<NonNullable<ButtonProps["onClick"]>>(
    (event) => {
      event.preventDefault();
      logoEditorToggle();
    },
    [logoEditorToggle]
  );

  // --- NEW ADVANCED FEATURE HANDLERS ---

  const handleAutoFullCorrection = useCallback(async () => {
    try {
      const resultBlobUrl = await AIprocessVideoFullCorrection();
      if (resultBlobUrl && videoRef) {
        videoRef.src = resultBlobUrl;
      }
    } catch (error) {
      console.error("Error processing AI video:", error);
    }
  }, [AIprocessVideoFullCorrection, videoRef]);

  const handleExtractAudio = useCallback(async () => {
    try {
      const resultBlobUrl = await extractAudio();
      if (resultBlobUrl) {
        const a = document.createElement("a");
        a.href = resultBlobUrl;
        a.download = "extracted_audio.mp3";
        a.click();
      }
    } catch (error) {
      console.error("Error extracting audio:", error);
    }
  }, [extractAudio]);

  const handleGenerateGif = useCallback(async () => {
    try {
      const resultBlobUrl = await generateGif(0, 5); 
      if (resultBlobUrl) {
        const a = document.createElement("a");
        a.href = resultBlobUrl;
        a.download = "video_preview.gif";
        a.click();
      }
    } catch (error) {
      console.error("Error generating GIF:", error);
    }
  }, [generateGif]);

  const handleAddBackgroundAudio = useCallback(() => {
    const inputElement = document.createElement("input");
    inputElement.type = "file";
    inputElement.accept = "audio/*";
    inputElement.style.display = "none";

    inputElement.onchange = async (event) => {
      const input = event.target as HTMLInputElement;
      if (input.files && input.files[0]) {
        const audioBlob = new Blob([input.files[0]], { type: input.files[0].type });
        const audioUrl = URL.createObjectURL(audioBlob);

        try {
          const resultBlobUrl = await addBackgroundAudio(audioUrl);
          if (resultBlobUrl && videoRef) {
            videoRef.src = resultBlobUrl; 
          }
          URL.revokeObjectURL(audioUrl);
        } catch (error) {
          console.error("Error adding audio:", error);
        }
      }
      inputElement.remove();
    };

    document.body.appendChild(inputElement);
    inputElement.click();
  }, [addBackgroundAudio, videoRef]);

  // --- SOCIAL SHARING ---

  const handleYouTubeUpload = useCallback(async () => {
    if (!videoRef?.src) {
      alert("No video available for upload!");
      return;
    }
    // TELEMETRY: Record intent to publish long-form content
    if (reportBehavior) reportBehavior("youtube_sharing", 5, "interest");
    window.open(`https://www.youtube.com/upload`, "_blank");
  }, [videoRef, reportBehavior]);

  const handleTikTokUpload = useCallback(async () => {
    if (!videoRef?.src) {
      alert("No video available for upload!");
      return;
    }
    // TELEMETRY: Record intent to publish short-form content
    if (reportBehavior) reportBehavior("tiktok_sharing", 5, "interest");
    window.open(`https://www.tiktok.com/upload`, "_blank");
  }, [videoRef, reportBehavior]);

  return (
    <Grid container spacing={0} py={1} gap={1}>
      <Grid size="grow" sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
        
        <Box sx={{ display: { xs: "block", md: "none", lineHeight: 1 } }}>
          <Tooltip arrow disableFocusListener placement="top" title="Toggle sidebar">
            <Button sx={{ minWidth: "32px" }} size="small" variant="outlined" onClick={handleMobileSidebarToggle}>
              <ViewSidebar />
            </Button>
          </Tooltip>
        </Box>

        <Button disabled={isProcessing} size="small" sx={{ textTransform: "none" }} variant="contained" onClick={handleLogoEditorToggle}>
          Logo Editor
        </Button>

        {/* --- NEW ADVANCED BUTTONS --- */}
        <Button 
          disabled={isProcessing} 
          size="small" 
          sx={{ 
            textTransform: "none",
            // ADAPTIVE UI: Pulse effect on the AI button if the user is struggling/frustrated
            animation: highlightCTA && !isProcessing ? "pulse 2s infinite" : "none",
            "@keyframes pulse": {
              "0%": { boxShadow: "0 0 0 0 rgba(156, 39, 176, 0.7)" }, // Secondary color pulse
              "70%": { boxShadow: "0 0 0 8px rgba(156, 39, 176, 0)" },
              "100%": { boxShadow: "0 0 0 0 rgba(156, 39, 176, 0)" }
            }
          }} 
          variant="contained" 
          onClick={handleAutoFullCorrection} 
          color="secondary"
        >
          <AutoFixHigh sx={{ mr: 1, fontSize: "18px" }} />
          AI Auto-Edit
        </Button>

        <Button disabled={isProcessing} size="small" sx={{ textTransform: "none" }} variant="contained" onClick={handleExtractAudio}>
          <GraphicEq sx={{ mr: 1, fontSize: "18px" }} />
          Extract MP3
        </Button>

        <Button disabled={isProcessing} size="small" sx={{ textTransform: "none" }} variant="contained" onClick={handleGenerateGif}>
          <Gif sx={{ mr: 1, fontSize: "22px" }} />
          Make GIF
        </Button>

        <Button disabled={isProcessing} size="small" sx={{ textTransform: "none" }} variant="contained" onClick={handleAddBackgroundAudio}>
          <AudioFile sx={{ mr: 1, fontSize: "18px" }} />
          Add BGM
        </Button>

        {/* --- SOCIAL BUTTONS --- */}
        <Button disabled={isProcessing} size="small" variant="outlined" onClick={handleYouTubeUpload} color="error">
          <YouTube sx={{ mr: 1 }} />
          YouTube
        </Button>

        <Button 
          disabled={isProcessing} 
          size="small" 
          variant="outlined" 
          onClick={handleTikTokUpload} 
          sx={{ 
            // ADAPTIVE UI: Fixes invisibility issue in Dark Mode
            color: isDarkMode ? "#ffffff" : "#000000", 
            borderColor: isDarkMode ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.5)",
            "&:hover": {
              borderColor: isDarkMode ? "#ffffff" : "#000000",
              backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.04)"
            }
          }}
        >
          <MusicNote sx={{ mr: 1 }} />
          TikTok
        </Button>

        {isProcessing && !!progress && (
          <Box sx={{ width: "100%", mt: 1 }}>
            <LinearProgressWithLabel value={progress} />
          </Box>
        )}
      </Grid>
    </Grid>
  );
};

export default VideoEditorToolbar;